import { Badge, Flex, Text, VStack } from '@chakra-ui/react'
import { useCachedUser } from '@hb/react'
import {
  Encounter,
  EncounterStatus,
  encounterTypeName,
  getDateString,
  getFullName,
} from '@hb/shared'
import { PersonIcon } from '@icons/PersonIcon'
import { useMemo } from 'react'
import { useEncounterStatusLabel } from './useEncounterStatusLabel'

const RenderingProviderPreview = ({ providerId }: { providerId: string | null }) => {
  const { data: provider, loading } = useCachedUser(providerId)

  const display = useMemo(() => {
    if (!providerId) return '—'
    if (loading) return 'Loading…'
    const name = getFullName(provider)
    return name || 'Unknown provider'
  }, [providerId, loading, provider])

  if (!providerId) return null

  return (
    <Flex align="center" gap={1}>
      <PersonIcon w={4} h={4} />
      <Text fontSize="md">{display}</Text>
    </Flex>
  )
}

const getStatusColorScheme = (status: EncounterStatus) => {
  switch (status) {
    case EncounterStatus.COMPLETED:
      return 'blue'
    case EncounterStatus.AUTHENTICATED:
      return 'green'
    case EncounterStatus.DRAFT:
      return 'gray'
  }
}

export const EncounterPreview = ({
  encounter,
  onClick,
}: {
  encounter: Encounter
  onClick?: () => void
}) => {
  const dateLabel = encounter.date ? getDateString(encounter.date, 'short', true) : '—'
  const statusLabel = useEncounterStatusLabel(encounter.status)

  return (
    <VStack
      borderRadius={4}
      shadow="md"
      bg="white"
      _dark={{ bg: 'gray.900' }}
      w="100%"
      p={2}
      align="stretch"
      spacing={0}
      onClick={onClick}
      cursor={onClick ? 'pointer' : undefined}
      transition="background 0.15s"
      _hover={onClick ? { bg: 'gray.50', _dark: { bg: 'gray.800' } } : undefined}>
      <Flex align="center" gap={1} w="100%" justify="space-between">
        <Text fontSize="md" fontWeight={500}>
          {dateLabel}
        </Text>
        <Badge fontSize="sm" colorScheme={getStatusColorScheme(encounter.status)}>
          {statusLabel}
        </Badge>
      </Flex>
      <Text fontSize="md" fontWeight={500}>
        {encounterTypeName[encounter.type]}
      </Text>
      <RenderingProviderPreview providerId={encounter.renderingProvider} />
    </VStack>
  )
}
